'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, Settings, Volume2, VolumeX } from 'lucide-react';
import type { MediaItem, VideoItem } from '@/types/pdp';

type Item =
  | { kind: 'image'; data: MediaItem }
  | { kind: 'video'; data: VideoItem };

interface Props {
  screenshots: MediaItem[];
  videos: VideoItem[];
}

interface QualityLevel { height: number; index: number }

export function MediaGallery({ screenshots, videos }: Props) {
  const items: Item[] = [
    ...videos.slice(0, 2).map(v => ({ kind: 'video' as const, data: v })),
    ...screenshots.slice(0, 10).map(s => ({ kind: 'image' as const, data: s })),
  ];

  const [active, setActive]       = useState(0);
  const [playing, setPlaying]     = useState(false);
  const [muted, setMuted]         = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [levels, setLevels]       = useState<QualityLevel[]>([]);
  const [curLevel, setCurLevel]   = useState(-1); // -1 = Auto
  const videoRef  = useRef<HTMLVideoElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hlsRef    = useRef<any>(null);
  const touchX    = useRef<number | null>(null);

  const goTo = useCallback((i: number) => {
    setPlaying(false);
    setQualityOpen(false);
    setLevels([]);
    setCurLevel(-1);
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    setActive(i);
  }, []);

  const prev = () => goTo((active - 1 + items.length) % items.length);
  const next = () => goTo((active + 1) % items.length);

  /* -- HLS player init -- */
  useEffect(() => {
    if (!playing) return;
    const cur = items[active];
    if (cur.kind !== 'video' || !cur.data.hls_url) return;
    const el = videoRef.current;
    if (!el) return;

    (async () => {
      try {
        const HlsModule = await import('hls.js');
        const Hls = HlsModule.default;
        if (!Hls.isSupported()) {
          el.src = cur.data.hls_url!;
          el.play().catch(() => {});
          return;
        }
        const hls = new Hls({ startLevel: -1 });
        hlsRef.current = hls;
        hls.loadSource(cur.data.hls_url!);
        hls.attachMedia(el);
        hls.on(Hls.Events.MANIFEST_PARSED, (_: unknown, data: { levels: { height: number }[] }) => {
          const lvls: QualityLevel[] = data.levels.map((l, i) => ({ height: l.height, index: i }));
          setLevels(lvls);
          hls.currentLevel = -1;
          el.play().catch(() => {});
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, (_: unknown, data: { level: number }) => {
          setCurLevel(data.level);
        });
      } catch {
        el.src = cur.data.hls_url!;
        el.play().catch(() => {});
      }
    })();

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, active]);

  function setQuality(idx: number) {
    if (hlsRef.current) hlsRef.current.currentLevel = idx;
    setCurLevel(idx);
    setQualityOpen(false);
  }

  function handleTouchStart(e: React.TouchEvent) { touchX.current = e.touches[0].clientX; }
  function handleTouchEnd(e: React.TouchEvent) {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next(); else prev();
  }

  if (items.length === 0) return null;
  const cur = items[active];

  const thumbSrc = (item: Item) =>
    item.kind === 'image' ? item.data.thumb : (item.data.preview_thumb ?? item.data.preview_url ?? '');
  const mainSrc  = (item: Item) =>
    item.kind === 'image' ? item.data.url   : (item.data.preview_url ?? '');

  const qualityLabel = (idx: number) => {
    if (idx === -1) return 'Авто';
    const l = levels.find(l => l.index === idx);
    return l ? `${l.height}p` : 'Авто';
  };

  return (
    <div className="media-gallery">
      {/* -- Main viewer -- */}
      <div
        className="mg-viewer"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image / video poster */}
        {(!playing || cur.kind === 'image') && (
          <img
            src={mainSrc(cur)}
            srcSet={
              cur.kind === 'image' && cur.data.w700 && cur.data.w1200
                ? `${cur.data.w700} 700w, ${cur.data.w1200} 1200w`
                : undefined
            }
            sizes="(max-width: 768px) 700px, 1200px"
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }}
          />
        )}

        {/* HLS video element (hidden until playing) */}
        {cur.kind === 'video' && (
          <video
            ref={videoRef}
            muted={muted}
            controls={playing}
            playsInline
            style={{
              width: '100%', height: '100%', display: playing ? 'block' : 'none',
              objectFit: 'cover', outline: 'none', position: 'absolute', inset: 0,
            }}
          />
        )}

        {/* Play overlay — shown when not playing and it's a video */}
        {cur.kind === 'video' && !playing && (
          <div
            className="mg-play-overlay"
            onClick={() => setPlaying(true)}
          >
            {/* Radial glow behind button */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(circle at center, rgba(82,176,67,.18) 0%, transparent 65%)',
              pointerEvents: 'none',
            }} />
            <button className="mg-play-btn" aria-label="Відтворити трейлер">
              <Play size={30} color="#fff" style={{ marginLeft: 4 }} />
            </button>
            {cur.data.caption && (
              <span style={{
                position: 'absolute', bottom: 18,
                fontSize: 11, fontWeight: 700, letterSpacing: '.14em',
                textTransform: 'uppercase', color: 'rgba(255,255,255,.6)',
                background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(6px)',
                padding: '4px 12px', borderRadius: 999,
              }}>
                {cur.data.caption}
              </span>
            )}
          </div>
        )}

        {/* Controls overlay — quality + mute when playing */}
        {cur.kind === 'video' && playing && (
          <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, zIndex: 10 }}>
            {/* Mute toggle */}
            <button
              onClick={() => { setMuted(m => !m); }}
              style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              {muted ? <VolumeX size={13} color="#fff" /> : <Volume2 size={13} color="#fff" />}
            </button>

            {/* Quality selector */}
            {levels.length > 1 && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setQualityOpen(o => !o)}
                  style={{ height: 32, padding: '0 10px', borderRadius: 8, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700 }}
                >
                  <Settings size={12} color="rgba(255,255,255,.8)" />
                  {qualityLabel(curLevel)}
                </button>
                {qualityOpen && (
                  <div style={{ position: 'absolute', top: 36, right: 0, background: 'rgba(10,14,10,.96)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, padding: 6, minWidth: 100, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button onClick={() => setQuality(-1)} style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: curLevel === -1 ? 'rgba(82,176,67,.15)' : 'transparent', color: curLevel === -1 ? '#52b043' : 'rgba(255,255,255,.7)', fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                      Авто
                    </button>
                    {[...levels].reverse().map(l => (
                      <button key={l.index} onClick={() => setQuality(l.index)} style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: curLevel === l.index ? 'rgba(82,176,67,.15)' : 'transparent', color: curLevel === l.index ? '#52b043' : 'rgba(255,255,255,.7)', fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                        {l.height}p
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Nav arrows */}
        {items.length > 1 && (
          <>
            <button onClick={e => { e.stopPropagation(); prev(); }} className="mg-arrow mg-arrow-l">
              <ChevronLeft size={16} />
            </button>
            <button onClick={e => { e.stopPropagation(); next(); }} className="mg-arrow mg-arrow-r">
              <ChevronRight size={16} />
            </button>
            <span style={{ position: 'absolute', bottom: 10, right: 12, fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,.6)', background: 'rgba(0,0,0,.55)', padding: '2px 8px', borderRadius: 999, backdropFilter: 'blur(6px)', zIndex: 2 }}>
              {active + 1} / {items.length}
            </span>
          </>
        )}
      </div>

      {/* -- Thumbnail strip -- */}
      {items.length > 1 && (
        <div className="media-gallery-thumbs">
          {items.map((item, i) => (
            <div
              key={i}
              onClick={() => goTo(i)}
              className={`mg-thumb${active === i ? ' mg-thumb-active' : ''}`}
            >
              <img src={thumbSrc(item)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {item.kind === 'video' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,.6)', border: '1px solid rgba(255,255,255,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Play size={10} color="#fff" style={{ marginLeft: 1 }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
